import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { Spinner } from '@/components/ui/Spinner';
import { SiteMasterSiteSwitcher } from '@/components/site-master/SiteMasterSiteSwitcher';
import { cn } from '@/lib/cn';

const navClass = (active: boolean) =>
  cn(
    'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-base font-semibold',
    active ? 'text-teal-700' : 'text-slate-500'
  );

export function SiteMasterLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, ok, master, currentSite, currentSiteId, sites, promotionAreas, refresh, setCurrentSite } =
    useSiteMasterSession();
  const hasMp = (promotionAreas?.length ?? 0) > 0;
  const allSitesMode = currentSiteId === 0;

  useEffect(() => {
    if (!loading && !ok) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      navigate(siteMasterPaths.login + (returnTo ? `?next=${encodeURIComponent(returnTo)}` : ''), {
        replace: true,
      });
    }
  }, [loading, ok, navigate]);

  async function switchSite(siteId: number) {
    try {
      const r = await apiFetch<{
        ok: boolean;
        current_site?: { id: number; site_code: string; site_name: string; enabled: boolean };
      }>('site_master_switch_site', {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId }),
      });
      if (r.ok) {
        setCurrentSite(r.current_site ?? null);
        await refresh();
      }
    } catch {
      /* ignore */
    }
  }

  async function logout() {
    await apiFetch('site_master_logout', { method: 'POST', body: '{}' });
    navigate(siteMasterPaths.login, { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spinner label="正在加载" />
      </div>
    );
  }

  if (!ok) {
    return null;
  }

  const path = location.pathname;

  return (
    <div
      className="flex min-h-screen flex-col bg-slate-50 pb-[calc(6.5rem+env(safe-area-inset-bottom))]"
      style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
    >
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold text-slate-900">
              {allSitesMode ? '全部站点' : (currentSite?.site_name ?? '选择站点')}
            </p>
            <p className="truncate text-base text-slate-600">
              {master?.display_name ? `${master.display_name} · ` : ''}
              {master?.phone}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="shrink-0 rounded-xl border-2 border-slate-200 px-4 py-2 text-base font-semibold text-slate-700"
          >
            退出
          </button>
        </div>
        {sites.length > 1 ? (
          <SiteMasterSiteSwitcher
            sites={sites}
            currentSiteId={currentSiteId}
            allSitesMode={allSitesMode}
            onSwitch={(siteId) => void switchSite(siteId)}
          />
        ) : null}
      </header>

      <main className="flex-1 px-4 py-4">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-slate-200 bg-white shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <Link to={siteMasterPaths.home} className={navClass(path === siteMasterPaths.home)}>
          <i className="fa-solid fa-house text-xl" aria-hidden />
          首页
        </Link>
        {sites.length > 0 ? (
          <>
            <Link
              to={siteMasterPaths.add}
              className={navClass(path === siteMasterPaths.add)}
            >
              <i className="fa-solid fa-plus text-xl" aria-hidden />
              站点
            </Link>
            <Link to={siteMasterPaths.entries} className={navClass(path.startsWith(siteMasterPaths.entries))}>
              <i className="fa-solid fa-list text-xl" aria-hidden />
              信息
            </Link>
          </>
        ) : null}
        {hasMp ? (
          <Link to={siteMasterPaths.mp} className={navClass(path === siteMasterPaths.mp)}>
            <i className="fa-solid fa-bullhorn text-xl" aria-hidden />
            微推广
          </Link>
        ) : null}
        <Link to={siteMasterPaths.profile} className={navClass(path === siteMasterPaths.profile)}>
          <i className="fa-solid fa-user text-xl" aria-hidden />
          我的
        </Link>
      </nav>
    </div>
  );
}
